import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import { useSnackbar } from 'notistack';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AsyncDialog, AsyncDialogRef } from '../../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialog, ConfirmationDialogData } from '../../../components/AsyncDialog/ConfirmationDialog';
import { Table } from '../../../components/Table';
import { TableRef } from '../../../components/Table/Table';
import { fetchStatuses } from '../../../constants/fetchStatuses';
import { useAppDispatch } from '../../../redux/hooks';
import {
  deleteCompanyAddress,
  fetchCompanyAddresses,
} from '../../../redux/modules/companyAddress/companyAddress.actions';
import { companyAddressSelector } from '../../../redux/modules/companyAddress/companyAddress.selectors';
import { CompanyAddress } from '../../../types/companyAddress';
import { CompanyAddressDialog } from './CompanyAddressDialog';

interface CompanyAddressesTableProps {
  companyId: number;
}

export function CompanyAddressesTable({ companyId }: CompanyAddressesTableProps) {
  const { t } = useTranslation('apiError');
  const dispatch = useAppDispatch();
  const tableRef = useRef<TableRef>(null);
  const [open, setOpen] = React.useState(false);
  const asyncDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const [editValue, setEditValue] = useState<CompanyAddress | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const fetchCompanyAddressesWithCompanyId = (params: any) => fetchCompanyAddresses({ companyId, ...params });

  const onDeleteItem = async (id: number) => {
    const isConfirmed = await asyncDialogRef.current?.show({
      title: "Delete company's address",
      description: 'Are you sure you want to delete this address?',
    });

    if (isConfirmed) {
      try {
        await dispatch(deleteCompanyAddress({ companyId, id })).unwrap();

        enqueueSnackbar('Address deleted successfully!', { variant: 'success' });
      } catch (err: any) {
        enqueueSnackbar(`Delete address failed: ${t(err?.message)} ${JSON.stringify(err?.data)}`, {
          variant: 'error',
        });
      } finally {
        tableRef?.current?.reload();
      }
    }
  };

  const onEditItem = (row: CompanyAddress): void => {
    setEditValue(row);
    setOpen(true);
  };

  const [columns] = useState<GridColDef[]>([
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key="delete"
          icon={<DeleteOutlineOutlinedIcon />}
          label="Delete"
          onClick={() => onDeleteItem(params.row.id)}
        />,
        <GridActionsCellItem
          key="edit"
          icon={<EditOutlinedIcon />}
          label="Edit"
          onClick={() => onEditItem(params.row)}
        />,
      ],
    },
    { field: 'id', headerName: 'Id' },
    { field: 'buildingNumber', headerName: 'Building Number' },
    { field: 'streetName', headerName: 'Street Name' },
    { field: 'district', headerName: 'District' },
    { field: 'address1', headerName: 'Address1', flex: 1 },
    { field: 'city', headerName: 'City' },
    { field: 'postCode', headerName: 'Post Code' },
    { field: 'additionalNumber', headerName: 'Additional Number' },
    { field: 'address2', headerName: 'Address2', flex: 1 },
    { field: 'unitNumber', headerName: 'Unit Number', width: 120 },
    { field: 'isPrimaryAddress', headerName: 'Primary', width: 100 },
    { field: 'locationCoordinates', headerName: 'Location Coordinates', width: 100 },
    {
      field: 'createdAt',
      headerName: 'Created',
      sortable: false,
      type: 'date',
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      sortable: false,
      type: 'date',
    },
  ]);

  const { totalCompanyAddresses, companyAddresses, fetchStatus } = useSelector(companyAddressSelector);

  const onAddNew = (): void => {
    setEditValue(null);
    setOpen(true);
  };

  const handleClose = (isReload: boolean) => {
    if (isReload) {
      tableRef?.current?.reload();
    }
    setEditValue(null);
    setOpen(false);
  };

  return (
    <Grid container flexDirection="column" paddingX={2} paddingBottom={3}>
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Addresses &nbsp;
          <Fab color="primary" size="small" aria-label="Create Action" onClick={onAddNew}>
            <AddIcon />
          </Fab>
        </Typography>
      </Box>
      <Grid item display="flex" sx={{ width: '100%' }}>
        <Table
          ref={tableRef}
          fetchItems={fetchCompanyAddressesWithCompanyId}
          rows={companyAddresses}
          columns={columns}
          loading={fetchStatus === fetchStatuses.pending}
          rowCount={totalCompanyAddresses}
          disableColumnFilter
          disableColumnMenu
          density="compact"
          initialColumnVisibilityModel={{
            id: false,
            buildingNumber: false,
            streetName: false,
            district: false,
            city: false,
            postCode: false,
            additionalNumber: false,
          }}
          wrapperHeight={300}
        />
      </Grid>

      <AsyncDialog ref={asyncDialogRef} dialog={ConfirmationDialog} />
      <CompanyAddressDialog companyId={companyId} open={open} initialValue={editValue} onClose={handleClose} />
    </Grid>
  );
}
